import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import TheatersOutlinedIcon from "@mui/icons-material/TheatersOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainHeading from "../components/main-heading.component";
import { useMainApi } from "../provider/main-api.provider";
import { _get } from "../services/api.service";
import "./content-engine-v2/content-engine.css";
import "./video-create/CreateVideo.css";
import {
  formatCardTimestamp,
  formatDuration,
  getStatusMeta,
  TERMINAL_STATUSES,
} from "./video-projects/projectUtils";
import "./Dashboard.css";
import "./video-projects/VideoProjects.css";

interface VideoProject {
  id: number;
  status: string;
  topic?: string;
  thumbnail_url?: string;
  duration: number | null;
  created_at: string;
}

function StatusBadge({ status }: { status: string }) {
  const meta = getStatusMeta(status);
  const isRendering = status === "rendering";
  return (
    <span
      className={`vp-status-badge${isRendering ? " rendering" : ""}`}
      style={{ color: meta.color, background: meta.bg }}
    >
      <span className="vp-status-dot" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

function ProjectCard({
  project,
  onOpen,
}: {
  project: VideoProject;
  onOpen: (id: number) => void;
}) {
  const title = project.topic?.trim() || `Video Project #${project.id}`;
  return (
    <button type="button" className="vp-project-card" onClick={() => onOpen(project.id)}>
      {project.thumbnail_url ? (
        <img src={project.thumbnail_url} alt="" className="vp-card-thumb" />
      ) : (
        <div className="vp-card-thumb-placeholder">
          <PlayCircleOutlineIcon sx={{ fontSize: 48 }} />
        </div>
      )}
      <div className="vp-card-gradient" />
      <div className="vp-card-status">
        <StatusBadge status={project.status} />
      </div>
      {project.duration != null && (
        <span className="vp-card-duration">
          <AccessTimeIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }} />
          {formatDuration(project.duration)}
        </span>
      )}
      <div className="vp-card-footer">
        <span className="vp-card-title">{title}</span>
        <span className="vp-card-meta">{formatCardTimestamp(project.created_at)}</span>
      </div>
    </button>
  );
}

const formatShortDate = (dateStr?: string | null) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { me, credits, subscription, hasSubscription, loading: profileLoading } = useMainApi();
  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    try {
      const data = await _get("video-engine/projects/", {}, true);
      setProjects(data ?? []);
    } catch (err) {
      console.error("Failed to load video projects", err);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const firstName = me?.first_name || "there";
  const creditBalance = credits?.combined.balance ?? 0;
  const totalCredits = credits?.combined.total ?? 0;

  const stats = useMemo(() => {
    const completed = projects.filter((p) => p.status === "completed").length;
    const inProgress = projects.filter((p) => !TERMINAL_STATUSES.includes(p.status)).length;
    return { total: projects.length, completed, inProgress };
  }, [projects]);

  const inProgressProjects = useMemo(
    () => projects.filter((p) => !TERMINAL_STATUSES.includes(p.status)),
    [projects],
  );

  const recentProjects = useMemo(
    () =>
      [...projects]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 6),
    [projects],
  );

  const loading = profileLoading || projectsLoading;

  const quickActions = [
    {
      title: "Quick Video",
      description: "Turn a topic into a short-form video in minutes with AI script and render.",
      route: "/quick-video",
      icon: <BoltOutlinedIcon sx={{ fontSize: 22 }} />,
      primary: true,
      cta: "Start creating",
    },
    {
      title: "Content Engine",
      description: "Generate scripts, refine hooks and list items, then send to video.",
      route: "/content-engine",
      icon: <AutoAwesomeIcon sx={{ fontSize: 22 }} />,
      primary: false,
      cta: "Open scripts",
    },
    {
      title: "Video Projects",
      description: "Browse all renders, track pipeline progress, and download finished videos.",
      route: "/video-projects",
      icon: <TheatersOutlinedIcon sx={{ fontSize: 22 }} />,
      primary: false,
      cta: "View projects",
    },
  ];

  return (
    <div className="container-fluid dashboard-page video-projects-page">
      <MainHeading
        title={`Welcome back, ${firstName}`}
        subTitle="Your studio overview — credits, projects, and quick starts"
      />

      <div className="db-layout">
        <section className="db-hero">
          <div className="db-hero-grid">
            <div className="db-hero-copy">
              <span className="db-hero-eyebrow">
                <BoltOutlinedIcon sx={{ fontSize: 14 }} />
                Video studio
              </span>
              <h2 className="db-hero-title">Turn your scripts into ready-to-post videos</h2>
              <p className="db-hero-text">
                Start from a script or topic, generate your video, and track every render in one place.
              </p>
              <div className="db-hero-actions">
                <button
                  type="button"
                  className="cv-btn cv-btn-primary"
                  onClick={() => navigate("/quick-video")}
                >
                  <BoltOutlinedIcon sx={{ fontSize: 18 }} />
                  New Quick Video
                </button>
                <button
                  type="button"
                  className="cv-btn cv-btn-secondary"
                  onClick={() => navigate("/video-projects")}
                >
                  <TheatersOutlinedIcon sx={{ fontSize: 18 }} />
                  My projects
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="db-stats">
          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon blue">
                <CreditCardOutlinedIcon sx={{ fontSize: 20 }} />
              </div>
              <TrendingUpIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
            </div>
            {loading ? (
              <div className="db-skeleton-card" style={{ height: 36, aspectRatio: "auto" }} />
            ) : (
              <>
                <div className="db-stat-value">
                  {creditBalance}
                  <span style={{ fontSize: 16, color: "#94a3b8", fontWeight: 700 }}>
                    {" "}
                    / {totalCredits}
                  </span>
                </div>
                <div className="db-stat-label">Credits available</div>
              </>
            )}
          </div>

          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon slate">
                <TheatersOutlinedIcon sx={{ fontSize: 20 }} />
              </div>
            </div>
            {loading ? (
              <div className="db-skeleton-card" style={{ height: 36, aspectRatio: "auto" }} />
            ) : (
              <>
                <div className="db-stat-value">{stats.total}</div>
                <div className="db-stat-label">Video projects</div>
              </>
            )}
          </div>

          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon green">
                <PlayCircleOutlineIcon sx={{ fontSize: 20 }} />
              </div>
            </div>
            {loading ? (
              <div className="db-skeleton-card" style={{ height: 36, aspectRatio: "auto" }} />
            ) : (
              <>
                <div className="db-stat-value">{stats.completed}</div>
                <div className="db-stat-label">Completed</div>
              </>
            )}
          </div>

          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon amber">
                <AccessTimeIcon sx={{ fontSize: 20 }} />
              </div>
            </div>
            {loading ? (
              <div className="db-skeleton-card" style={{ height: 36, aspectRatio: "auto" }} />
            ) : (
              <>
                <div className="db-stat-value">{stats.inProgress}</div>
                <div className="db-stat-label">In progress</div>
                {stats.inProgress > 0 && (
                  <div className="db-stat-sub">Pipeline or render running</div>
                )}
              </>
            )}
          </div>
        </section>

        {!loading && inProgressProjects.length > 0 && (
          <section className="db-processing-banner">
            <div className="db-processing-copy">
              <span className="db-processing-dot" aria-hidden />
              <div>
                <p className="db-processing-title">
                  {inProgressProjects.length} project
                  {inProgressProjects.length === 1 ? "" : "s"} currently processing
                </p>
                <p className="db-processing-text">
                  {inProgressProjects[0].topic?.trim() || `Project #${inProgressProjects[0].id}`}
                  {inProgressProjects.length > 1
                    ? ` and ${inProgressProjects.length - 1} more`
                    : ""}{" "}
                  — open to track progress live.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="cv-btn cv-btn-primary"
              onClick={() => navigate(`/video-projects/${inProgressProjects[0].id}`)}
            >
              View progress
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </button>
          </section>
        )}

        <section>
          <div className="db-section-head">
            <div>
              <h3 className="db-section-title">Quick start</h3>
              <p className="db-section-sub">Jump into the workflow you need</p>
            </div>
          </div>
          <div className="db-actions-grid">
            {quickActions.map((action) => (
              <button
                key={action.title}
                type="button"
                className={`db-action-card${action.primary ? " primary" : ""}`}
                onClick={() => navigate(action.route)}
              >
                <div className="db-action-icon">{action.icon}</div>
                <h4 className="db-action-title">{action.title}</h4>
                <p className="db-action-text">{action.description}</p>
                <span className="db-action-foot">
                  {action.cta}
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </span>
              </button>
            ))}
          </div>
        </section>

        <section>
          <div className="db-section-head">
            <div>
              <h3 className="db-section-title">Recent projects</h3>
              <p className="db-section-sub">Latest videos from your studio</p>
            </div>
            {recentProjects.length > 0 && (
              <button
                type="button"
                className="db-link-btn"
                onClick={() => navigate("/video-projects")}
              >
                View all
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </div>

          {projectsLoading ? (
            <div className="db-recent-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="db-skeleton-card" />
              ))}
            </div>
          ) : recentProjects.length > 0 ? (
            <div className="db-recent-grid">
              {recentProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onOpen={(id) => navigate(`/video-projects/${id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="db-empty">
              <div className="db-empty-icon">
                <PlayCircleOutlineIcon sx={{ fontSize: 28 }} />
              </div>
              <h4 className="db-empty-title">No video projects yet</h4>
              <p className="db-empty-text">
                Create your first quick video or turn a script into a render. Finished projects
                will show up here with previews and status.
              </p>
              <button
                type="button"
                className="cv-btn cv-btn-primary"
                onClick={() => navigate("/quick-video")}
              >
                <BoltOutlinedIcon sx={{ fontSize: 18 }} />
                Create Quick Video
              </button>
            </div>
          )}
        </section>

        {!loading && (
          <section className="db-stat-card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div className="db-stat-icon blue">
                <WorkspacePremiumOutlinedIcon sx={{ fontSize: 20 }} />
              </div>
              <div>
                <div className="db-stat-label" style={{ marginTop: 0 }}>
                  Plan
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "var(--vp-text)" }}>
                  {hasSubscription ? subscription?.plan_name ?? "Active subscription" : "Free plan"}
                </div>
                <div className="db-stat-sub">
                  {hasSubscription && subscription?.current_billing_end
                    ? `Renews ${formatShortDate(subscription.current_billing_end)}`
                    : "Upgrade for more credits and premium features"}
                </div>
              </div>
            </div>
            {!hasSubscription && (
              <button type="button" className="cv-btn cv-btn-secondary" onClick={() => navigate("/plans")}>
                View plans
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
